import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Loader2, ClipboardList, BadgeCheck, AlertTriangle, ShieldCheck, Receipt, Calendar, IndianRupee, TrendingUp, Users } from "lucide-react";
import { toast } from "sonner";
import { fmtINR, KpiTile, BreakdownTable, PeriodAndPropertyFilter, ScopeBadge } from "@/components/app/DashboardShared";

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function HrDashboard() {
  const [periodMonth, setPeriodMonth] = useState(currentMonth());
  const [propertyId, setPropertyId] = useState("");
  const [data, setData] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period_month: periodMonth });
      if (propertyId) params.set("property_id", propertyId);
      const [res, propsRes] = await Promise.all([
        api.get(`/dashboards/payroll?${params.toString()}`),
        api.get("/properties").catch(() => ({ data: [] })),
      ]);
      setData(res.data);
      setProperties(propsRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [periodMonth, propertyId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-20 text-zinc-400">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const t = data.totals;
  const pa = data.pending_approvals;
  const ls = data.leave_summary;
  const ac = data.attendance_compliance;
  const processedPct = t.employees_active
    ? Math.round((t.payslips_created / t.employees_active) * 100)
    : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="label-overline">People & Payroll</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">HR Dashboard</h1>
          <div className="mt-2 flex items-center gap-2">
            <ScopeBadge propertyId={propertyId} properties={properties} />
          </div>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Live view of salary processing, pending approvals, leaves, overtime, and payroll cost split by property and department.
          </p>
        </div>
        <PeriodAndPropertyFilter
          periodMonth={periodMonth}
          setPeriodMonth={setPeriodMonth}
          propertyId={propertyId}
          setPropertyId={setPropertyId}
          properties={properties}
        />
      </div>

      {/* Salary processing status */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile
          label="Salary Processed"
          value={`${t.payslips_created} / ${t.employees_active}`}
          accent={processedPct >= 90 ? "green" : processedPct >= 50 ? "amber" : "red"}
          sub={`${processedPct}% of active employees`}
        />
        <KpiTile label="Pending Payslips" value={t.payslips_pending} accent={t.payslips_pending > 0 ? "amber" : "green"} />
        <KpiTile label="Total Payroll (Net)" value={fmtINR(t.total_net)} accent="blue" sub={`Gross/CTC ${fmtINR(t.total_gross)}`} />
        <KpiTile label="Total OT Payout" value={fmtINR(t.total_ot_amount)} sub={`${t.total_ot_hours} hrs across team`} />
      </div>

      {/* Pending approvals */}
      <div>
        <div className="label-overline mb-2">Pending Approvals</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <ApprovalCard label="Leaves" value={pa.leaves} icon={Calendar} to="/leaves" />
          <ApprovalCard label="Advances" value={pa.advances} icon={Receipt} to="/advances" />
          <ApprovalCard label="KYC verification" value={pa.kyc} icon={ShieldCheck} to="/pending-approvals" />
          <ApprovalCard label="Apologies" value={pa.apologies} icon={ClipboardList} to="/warnings-admin" />
        </div>
      </div>

      {/* OT + Leave summary */}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="bg-white border border-zinc-200 rounded-md p-4">
          <div className="label-overline">Leave Summary · {periodMonth}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
            <LegendCell label="EL" value={ls.el} color="emerald" />
            <LegendCell label="SL" value={ls.sl} color="amber" />
            <LegendCell label="CL" value={ls.cl} color="sky" />
            <LegendCell label="LOP" value={ls.lop} color="red" />
            <LegendCell label="Weekly Off" value={ls.weekly_off} color="zinc" />
            <LegendCell label="Other" value={ls.uncategorised} color="zinc" />
          </div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-md p-4">
          <div className="flex items-center justify-between">
            <div className="label-overline">OT Summary · {periodMonth}</div>
            <div className="text-xs text-zinc-500">{data.ot_summary.total_hours} hrs · {fmtINR(data.ot_summary.total_amount)}</div>
          </div>
          <div className="mt-2 space-y-1.5">
            {data.ot_summary.top_employees.length === 0 ? (
              <div className="text-xs text-zinc-400 py-4 text-center">No overtime recorded.</div>
            ) : (
              data.ot_summary.top_employees.map((r) => (
                <div key={r.user_id} className="flex items-center justify-between text-sm">
                  <span className="truncate">{r.name}</span>
                  <span className="font-mono text-zinc-600">{r.ot_hours} hrs · {fmtINR(r.ot_amount)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Attendance compliance + High OT alerts */}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="bg-white border border-zinc-200 rounded-md p-4">
          <div className="flex items-center gap-2">
            <BadgeCheck className="w-4 h-4 text-emerald-600" />
            <div className="label-overline">Attendance Compliance (today)</div>
          </div>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
            <LegendCell label="Present" value={ac.today_present} color="emerald" />
            <LegendCell label="Absent" value={ac.today_absent} color="red" />
            <LegendCell label="Weekly Off" value={ac.today_off} color="zinc" />
          </div>
          <div className="mt-3 text-center text-3xl font-black font-display tracking-tight">
            {ac.compliance_pct}%
            <span className="text-xs font-medium text-zinc-500 ml-2">compliance</span>
          </div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-md p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <div className="label-overline">High OT alerts (≥ {data.high_ot_threshold} hrs)</div>
          </div>
          {data.high_ot_alerts.length === 0 ? (
            <div className="py-6 text-center text-xs text-zinc-400">No one crossed the threshold this month.</div>
          ) : (
            <div className="mt-2 space-y-1.5">
              {data.high_ot_alerts.map((r) => (
                <div key={r.user_id} className="flex items-center justify-between text-sm" data-testid={`high-ot-${r.user_id}`}>
                  <span className="truncate">{r.name}</span>
                  <span className="font-mono font-semibold text-amber-700">{r.ot_hours} hrs</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Breakdown tables */}
      <BreakdownTable
        title="Payroll Cost by Property"
        rows={data.by_property}
        colKey="property_name"
        colLabel="Property"
      />
      <BreakdownTable
        title="Payroll Cost by Department"
        rows={data.by_department}
        colKey="department"
        colLabel="Department"
      />
    </div>
  );
}

function ApprovalCard({ label, value, icon: Icon, to }) {
  const tone = value > 0 ? "border-amber-300 bg-amber-50" : "border-zinc-200 bg-white";
  return (
    <Link to={to} className={`block border ${tone} rounded-md p-4 hover:shadow-sm transition-shadow`} data-testid={`approval-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-center justify-between">
        <Icon className="w-4 h-4 text-zinc-500" />
        <div className="font-display text-2xl font-black">{value}</div>
      </div>
      <div className="text-xs text-zinc-600 mt-1">{label}</div>
    </Link>
  );
}

function LegendCell({ label, value, color }) {
  const palette = {
    emerald: "bg-emerald-50 text-emerald-900 border-emerald-200",
    amber: "bg-amber-50 text-amber-900 border-amber-200",
    sky: "bg-sky-50 text-sky-900 border-sky-200",
    red: "bg-red-50 text-red-900 border-red-200",
    zinc: "bg-zinc-50 text-zinc-700 border-zinc-200",
  };
  return (
    <div className={`rounded border ${palette[color] || palette.zinc} px-2 py-1.5 text-center`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="font-display font-bold text-lg">{value}</div>
    </div>
  );
}
